import React, { PropsWithChildren, useLayoutEffect } from 'react';

import { Dimensions, StyleSheet, View } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { CardModal } from 'components/card-modal';
import { ModalHeaderProps } from 'components/card-modal-header';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { isWeb } from 'utils/environment';

import { useProductDetailCardModalStyles } from './use-product-detail-card-modal-styles';

type ProductDetailCardModalProps = PropsWithChildren<
  ModalHeaderProps & {
    testID?: string;
    analyticsEvent?: string | null;
    standardPadding?: boolean;
    alwaysShowHeader?: boolean;
    alwaysHideHeader?: boolean;
    webAnimate?: boolean;
    noDesktopWebMaxWidth?: boolean;
    disableGlobalAppModals?: boolean;
    hasInternalFooter?: boolean;
    keyboardAwareFooter?: boolean;
    isFullScreen?: boolean;
  }
>;

/** Matches CardModal / Reanimated web SlideInDown default duration. */
const MOBILE_WEB_ENTER_DURATION_MS = 300;

/**
 * On mobile web, root `(modals)` already runs `slide_from_bottom` while CardModal also
 * runs Reanimated `SlideInDown`. Nested transforms glitch so the sheet looks like it
 * never slides. Desktop is fine with CardModal's animation.
 *
 * For PDP only: disable CardModal's web enter and slide this shell via `marginTop`
 * (not `transform`) so it is not fighting the navigator transition.
 */
export function ProductDetailCardModal({
  children,
  webAnimate,
  ...cardModalProps
}: ProductDetailCardModalProps) {
  const productDetailStyles = useProductDetailCardModalStyles();
  const isMobile = useIsMobileBreakpoint();

  const enableMobileWebEnter = isWeb && isMobile && webAnimate !== false;

  const enterOffset = useSharedValue(enableMobileWebEnter ? Dimensions.get('window').height : 0);
  // Mobile keyboard and browser chrome resize the visual viewport; entrance must not replay.
  const shouldAnimateMobileWebEnterRef = React.useRef(enableMobileWebEnter);
  const hasAnimatedMobileWebEnterRef = React.useRef(false);

  useLayoutEffect(() => {
    if (!enableMobileWebEnter || !shouldAnimateMobileWebEnterRef.current) {
      enterOffset.value = 0;
      return;
    }

    if (hasAnimatedMobileWebEnterRef.current) {
      return;
    }

    hasAnimatedMobileWebEnterRef.current = true;
    enterOffset.value = Dimensions.get('window').height;
    enterOffset.value = withTiming(0, { duration: MOBILE_WEB_ENTER_DURATION_MS });
  }, [enableMobileWebEnter, enterOffset]);

  const mobileWebEnterStyle = useAnimatedStyle(() => ({
    marginTop: enterOffset.value,
  }));

  const modal = (
    <CardModal {...cardModalProps} webAnimate={enableMobileWebEnter ? false : webAnimate}>
      {children}
    </CardModal>
  );

  // Keep the web wrapper stable when responsive breakpoints change so PDP state is preserved.
  if (isWeb) {
    return (
      <Animated.View style={[styles.shell, productDetailStyles, mobileWebEnterStyle]}>
        {modal}
      </Animated.View>
    );
  }

  if (!productDetailStyles) {
    return modal;
  }

  return <View style={[styles.shell, productDetailStyles]}>{modal}</View>;
}

const styles = StyleSheet.create({
  shell: {
    flex: 1,
  },
});
